import { useState, useEffect } from "react";
import { getUserData } from "../../../infrastructure/storage/tokenStorage";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import ToastAlert from "../../components/common/ToastAlert";

const CreateUserPopup = ({ onClose, onSuccess }) => {
  const { createUser, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("MANAGER");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const result = await createUser(email, role);

    if (result.success) {
      onSuccess(result.message);
      onClose();
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-primary-dark rounded-2xl p-5 sm:p-6 w-full max-w-md border border-gray-700 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-white transition"
        >
          <svg
            className="w-5 h-5 sm:w-6 sm:h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h3 className="text-lg sm:text-xl font-semibold text-white mb-4">
          Create New User
        </h3>

        {error && (
          <div className="mb-4 p-2 sm:p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-xs sm:text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-400 text-xs sm:text-sm mb-1 sm:mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm sm:text-base focus:outline-none focus:border-primary-cyan"
              placeholder="user@example.com"
            />
          </div>

          <div>
            <label className="block text-gray-400 text-xs sm:text-sm mb-1 sm:mb-2">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm sm:text-base focus:outline-none focus:border-primary-cyan"
            >
              <option value="MANAGER">MANAGER</option>
              <option value="TECHNICAL">TECHNICAL</option>
              <option value="FINANCE">FINANCE</option>
            </select>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary-cyan hover:bg-primary-cyan/80 disabled:opacity-50 text-white px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base transition"
            >
              {loading ? "Creating..." : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ChangeRolePopup = ({ onClose, onSuccess }) => {
  const { getAllUsersExceptMe, changeUserRole, loading } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState({});
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(null);
  const currentUser = getUserData();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const result = await getAllUsersExceptMe();
    if (result.success) {
      setUsers(result.users);
      const initialRoles = {};
      result.users.forEach((user) => {
        initialRoles[user.email] = user.role;
      });
      setSelectedRoles(initialRoles);
    } else {
      setError(result.error);
    }
  };

  const getAvailableRoles = () => {
    const currentUserRole = currentUser?.role;
    if (currentUserRole === "SUPER_ADMIN") {
      return ["MANAGER", "TECHNICAL", "FINANCE"];
    } else if (currentUserRole === "MANAGER") {
      return ["TECHNICAL", "FINANCE"];
    }
    return [];
  };

  const handleRoleChange = async (userEmail, newRole) => {
    setUpdating(userEmail);
    setError("");

    const result = await changeUserRole(userEmail, newRole);

    if (result.success) {
      setSelectedRoles((prev) => ({ ...prev, [userEmail]: newRole }));
      onSuccess(`Role changed successfully for ${userEmail}`);
      await fetchUsers();
    } else {
      setError(result.error);
    }

    setUpdating(null);
  };

  const availableRoles = getAvailableRoles();

  if (users.length === 0 && !error) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-primary-dark rounded-2xl p-5 sm:p-6 w-full max-w-md border border-gray-700">
          <div className="flex justify-center items-center h-32">
            <div className="text-white">Loading users...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-primary-dark rounded-2xl p-5 sm:p-6 w-full max-w-2xl border border-gray-700 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-white transition"
        >
          <svg
            className="w-5 h-5 sm:w-6 sm:h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h3 className="text-lg sm:text-xl font-semibold text-white mb-4">
          Change User Roles
        </h3>

        {error && (
          <div className="mb-4 p-2 sm:p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-xs sm:text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {users.map((user) => (
            <div
              key={user.email}
              className="bg-gray-800 rounded-lg p-4 border border-gray-700"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1">
                  <p className="text-gray-400 text-xs">Email</p>
                  <p className="text-white text-sm font-medium">{user.email}</p>
                  <p className="text-gray-400 text-xs mt-1">Current Role</p>
                  <p className="text-primary-cyan text-sm font-medium capitalize">
                    {user.role}
                  </p>
                </div>

                <div className="flex-1">
                  <label className="block text-gray-400 text-xs mb-1">
                    New Role
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={selectedRoles[user.email] || user.role}
                      onChange={(e) =>
                        handleRoleChange(user.email, e.target.value)
                      }
                      disabled={updating === user.email}
                      className="flex-1 bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-primary-cyan disabled:opacity-50"
                    >
                      {availableRoles.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                    {updating === user.email && (
                      <div className="flex items-center">
                        <div className="w-5 h-5 border-2 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {users.length === 0 && !error && (
            <div className="text-center py-8 text-gray-400">
              No other users found
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-5 mt-2">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ProfileSection = () => {
  const [userData, setUserData] = useState(null);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isChangeRoleOpen, setIsChangeRoleOpen] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const data = getUserData();
    setUserData(data);
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 5000);
  };

  const handleCreateUserSuccess = (message) => {
    showToast(message, "success");
  };

  const handleRoleChangeSuccess = (message) => {
    showToast(message, "success");
  };

  if (!userData) return null;

  const firstLetter = userData.email?.charAt(0).toUpperCase() || "U";
  const canManageRoles =
    userData.role === "SUPER_ADMIN" || userData.role === "MANAGER";
  const canCreateUser = userData.role === "SUPER_ADMIN";

  const permissions = [
    "Change scheduler settings",
    "Change IP & domain name",
    "See financial metrics",
    "See performance metrics",
    "See security module",
    "See troubleshoot module",
  ];

  if (userData.role === "SUPER_ADMIN") {
    permissions.unshift("Create user", "Change any user role");
  } else if (userData.role === "MANAGER") {
    permissions.unshift("Change user role (TECHNICAL/FINANCE only)");
  }

  return (
    <div className="py-4 sm:py-6">
      {toast.show && (
        <ToastAlert
          message={toast.message}
          type={toast.type}
          onClose={() =>
            setToast({ show: false, message: "", type: "success" })
          }
        />
      )}

      <div className="bg-primary-dark rounded-xl sm:rounded-2xl p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 sm:gap-6">
          <div className="flex-shrink-0 self-center lg:self-auto">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-primary-cyan bg-primary-cyan flex items-center justify-center">
              <span className="text-white text-lg sm:text-xl font-medium">
                {firstLetter}
              </span>
            </div>
          </div>

          <div className="flex-1 text-center lg:text-left">
            <p className="text-gray-300 text-xs sm:text-sm">Email</p>
            <p className="text-white font-medium text-sm sm:text-base break-all mb-2">
              {userData.email}
            </p>
            <p className="text-gray-300 text-xs sm:text-sm">Role</p>
            <p className="text-white font-medium text-sm sm:text-base capitalize">
              {userData.role || "Admin"}
            </p>
          </div>

          <div className="flex-1">
            <div className="border border-gray-700 rounded-xl p-3 sm:p-4">
              <p className="text-primary-cyan text-xs sm:text-sm font-medium mb-2">
                What this role can do
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 sm:gap-x-4 gap-y-1">
                {permissions.map((perm, idx) => (
                  <div key={idx} className="flex items-center gap-1 sm:gap-2">
                    <span className="text-primary-cyan text-[10px] sm:text-xs">
                      •
                    </span>
                    <span className="text-gray-300 text-[10px] sm:text-sm break-words">
                      {perm}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-row lg:flex-col gap-2 sm:gap-3 flex-shrink-0 justify-center lg:justify-start">
            <button
              className="bg-primary-cyan text-white px-4 sm:px-5 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm hover:bg-primary-cyan/80 transition"
              onClick={() => navigate("/changePassword")}
            >
              Change password
            </button>

            {canCreateUser && (
              <button
                className="bg-primary-cyan text-white px-4 sm:px-5 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm hover:bg-primary-cyan/80 transition"
                onClick={() => setIsCreateUserOpen(true)}
              >
                Create user
              </button>
            )}

            {canManageRoles && (
              <button
                className="bg-primary-cyan text-white px-4 sm:px-5 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm hover:bg-primary-cyan/80 transition"
                onClick={() => setIsChangeRoleOpen(true)}
              >
                Change role
              </button>
            )}
          </div>
        </div>
      </div>

      {isCreateUserOpen && (
        <CreateUserPopup
          onClose={() => setIsCreateUserOpen(false)}
          onSuccess={handleCreateUserSuccess}
        />
      )}

      {isChangeRoleOpen && (
        <ChangeRolePopup
          onClose={() => setIsChangeRoleOpen(false)}
          onSuccess={handleRoleChangeSuccess}
        />
      )}
    </div>
  );
};

export default ProfileSection;
